import type { PaginationType } from '../types/Pagination.types';
import z from 'zod';
import { DateFilterOperatorSchema } from '../shared/schema';

const describeSchema = <T extends z.ZodTypeAny>(name: string, schema: T) =>
  schema.describe(name);

export const QuestDifficultyEnumSchema = describeSchema('QuestDifficultyEnumSchema', z.enum(['easy', 'medium', 'hard']));

export const QuestProgressTypeEnumSchema = describeSchema('QuestProgressTypeEnumSchema', z.enum(['streak', 'count', 'sum']));

// The backend can introduce new recurrence frequencies before the frontend maps
// them to a mission group, so keep response validation forward-compatible.
export const QuestRecurrenceEnumSchema = describeSchema('QuestRecurrenceEnumSchema', z.string().min(1));

// API: values filling the template's `refValue.*` `$param` nodes, in integer minor units (e.g. `{ amount: 700 }`).
// Keys vary per quest template (`betAmountFrom`, `betCount`, `amount`, ...), so this is a numeric map, not a fixed shape.
export const QuestReferenceValueSchema = describeSchema('QuestReferenceValueSchema', z.record(z.string(), z.number()));

export const QuestParamsSchema = z.object({
  filters: z.object({
    startsAt: DateFilterOperatorSchema.optional(),
    endsAt: DateFilterOperatorSchema.optional(),
  }).optional(),
});

export const QuestProgressSchema = describeSchema('QuestProgressSchema', z.object({
  field: z.string().nullish(),
  target: z.number(),
  increment: z.array(z.string()).optional(),
  type: QuestProgressTypeEnumSchema,
}));

export const QuestRecurrenceSchema = describeSchema('QuestRecurrenceSchema', z.object({
  freq: QuestRecurrenceEnumSchema,
}));

export const QuestTemplateSchema = describeSchema('QuestTemplateSchema', z.object({
  xpAmount: z.number(),
  recurrence: QuestRecurrenceSchema,
  recurrenceType: z.string(),
}));

export const QuestFilterSchema = describeSchema('QuestFilterSchema', z.object({
  type: z.string(),
  value: z.string(),
  label: z.string(),
}));

export const QuestSchema = describeSchema('QuestSchema', z.object({
  createdAt: z.string(),
  date: z.string(),
  difficulty: QuestDifficultyEnumSchema.nullish(),
  id: z.string(),
  level: z.number(),
  xpAmount: z.number(),
  name: z.string(),
  questTemplateId: z.string(),
  questTemplate: QuestTemplateSchema,
  slot: z.number(),
  // simplified games filters fields
  filters: z.array(QuestFilterSchema).nullish(),
  updatedAt: z.string(),
  progress: QuestProgressSchema,
  // API marks this "required" in Swagger, but the backend omits the key entirely (not `null`)
  // for quest templates that take no reference value — see docs-json vs. real response mismatch.
  referenceValue: QuestReferenceValueSchema.nullish(),
}));

export const UserQuestDtoSchema = describeSchema('UserQuestDtoSchema', z.object({
  // TODO: change schema, when api will be ready
  // now a lot fields contains in response, but we need only a few of them
  completedAt: z.string().nullish(),
  createdAt: z.string(),
  cycle: z.number(),
  endsAt: z.string(),
  id: z.string(),
  iteration: z.number(),
  progress: z.number(),
  questId: z.string(),
  startsAt: z.string(),
  updatedAt: z.string(),
  userId: z.string(),
  quest: QuestSchema,
}));

export type UserQuestDto = z.infer<typeof UserQuestDtoSchema>;
export type UserQuestListDto = PaginationType<UserQuestDto>;
export type UserQuestParams = z.infer<typeof QuestParamsSchema>;
export type QuestFilter = z.infer<typeof QuestFilterSchema>;
